import { Component, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { Input, }  from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { Place } from '@app/modules/model/place';
import { Ticket } from '@app/modules/model/ticket';
import { TicketProduct } from '@app/modules/model/ticketproduct';

import { platformService } from '@app/modules/common/platform'; 
import { languageSupport, languageService } from '@app/modules/common/language'
import { printService } from '@app/modules/print';
import { viewService } from '@app/modules/view';

@Component({
    selector: 'ui-change-print',
    templateUrl:'./ui-change.html',
    styleUrls: [ 
        './ui-change.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiTicketChange extends languageSupport implements OnInit, OnChanges, OnDestroy {
    @Input('ticket') _ticket: Ticket = null;
    @Input('source') _source = null;    // this component source (for debugging purposes)

    private _onprinting = false;
    private _destroyed = false;
    get _printing(){
        return this._onprinting;
    }

    set _printing(value){
        this._onprinting = value;
        // El setTimeout del print puede correr despues de ngOnDestroy
        // (sobrevive al cierre del editor por diseño — ver _OnChangePrint).
        // Llamar detectChanges() sobre una view destruida tira
        // ViewDestroyedError y rompe el finally del print.
        if (this._destroyed){
            return;
        }
        try {
            this.change.detectChanges();
        } catch(e){
            // ViewDestroyedError race: ngOnDestroy puede correr entre
            // el check de _destroyed y la llamada. Tragamos el error
            // (la flag se sincronizara en el proximo ciclo) en vez de
            // romper el caller.
        }
    }

    private _printerlog = true;
    private _PrinterLog(_changeinfo){
        if (!this._printerlog){
            return;
        }

        if (Object.keys(_changeinfo.printer).length > 0){
            if (_changeinfo.toccl){
                console.info("[TICKET CHANGE] TICKET CANCELATION (" + _changeinfo.order + ")");
            }
            else {
                if (_changeinfo.isnew){
                    console.info("[TICKET CHANGE] TICKET CREATED (" + _changeinfo.order + ")");
                }
                else {
                    console.info("[TICKET CHANGE] TICKET UPDATED (" + _changeinfo.order + ")");
                }
            }

            console.info("                COMPONENT: [" + this._source + "]")
            for(let _device in _changeinfo.printer){
                console.info("                DEVICE: [" + _device + "]")

                let _toinsert = _changeinfo.printer[_device].toInsert;
                for(let _item of _toinsert){
                    if (!_item.separator){
                        console.info("                + " + _item.count + " x " + _item.value['product']);
                    }
                }

                let _todelete = _changeinfo.printer[_device].toDelete;
                for(let _item of _todelete){
                    if (!_item.separator){
                        console.info("                - " + _item.count + " x " + _item.value['product']);
                    }
                }

                let _tomodify = _changeinfo.printer[_device].toModify || [];
                for(let _item of _tomodify){
                    if (!_item.separator){
                        console.info("                ~ " + _item.count + " x " + _item.value['product']);
                    }
                }
            }
        }
    }

    private _isSameChange(_toinsert, _todelete){
        if (_toinsert.product != _todelete.product){
            return false;   // not same product
        }

        if (_toinsert.options.length != _todelete.options.length){
            return false;   // not same number of options
        }

        if (_toinsert.weight != _todelete.weight){
            return false;   // not same weight
        }

        if (_toinsert.comments != _todelete.comments){
            return false;   // not same comments
        }

        return _toinsert.options.every(
        _insertoption => {
            let _found = false;
            for(let _deleteoption of _todelete.options){
                if (_deleteoption.option == _insertoption.option){
                    _found = true;      
                }
            }
            return _found;     // same option is found
        });
    }

    private _ClearVoidChanges(){
        if (!this._ticketchanges){
            return;     // no changes has been notified
        }
        
        for(let _toinsert of this._ticketchanges.toInsert){
            let _void = false;
            for(let _todelete of this._ticketchanges.toDelete){
                if (('void' in _todelete) && (_todelete['void'] == true)){
                    continue;   // skip: already marked
                }

                _void = this._isSameChange(_toinsert, _todelete); 
                if (_void){     // _todelete and _toinsert are the same change
                    _toinsert['void'] = true;
                    _todelete['void'] = true;

                    break;      // this item has already been marked
                }
            }
        }

        let _toInsert = [];
        for(let _toinsert of this._ticketchanges.toInsert){
            if (!('void' in _toinsert) || (_toinsert['void'] != true)){
                _toInsert.push(_toinsert);
            }
        }

        let _toDelete = [];
        for(let _todelete of this._ticketchanges.toDelete){
            if (!('void' in _todelete) || (_todelete['void'] != true)){
                _toDelete.push(_todelete);
            }
        }

        this._ticketchanges.toInsert = _toInsert;
        this._ticketchanges.toDelete = _toDelete;
    }

    private UntrustedHtml(elem){
        const scriptid = 'embedded-product-script';
        const existing = elem.querySelector(`script[data-id="${scriptid}"]`);
        if (existing) {
            return elem;
        }

        const jscode = `
            function _embedded_product_click(element){
                const row = element.closest('tr');
                if (row) {
                    const isToggled = row.getAttribute('data-toggled') === 'true';
                    if (isToggled) {
                        row.style.backgroundColor = '';
                        row.style.textDecoration = '';
                        row.setAttribute('data-toggled', 'false');
                    } 
                    else {
                        row.style.backgroundColor = 'lightgray';
                        row.style.textDecoration = 'line-through';
                        row.setAttribute('data-toggled', 'true');
                    }
                }
            }
        `;

        const script = document.createElement('script');

        script.type = 'text/javascript';
        script.textContent = jscode;
        script.setAttribute('data-id', scriptid);
        
        elem.appendChild(script);
        
        return elem;
    }

    @ViewChild('printable', {static: false}) _printable: ElementRef;
    private _OnChangePrint(data?: any){
        if (this._printing){
            return;     // avoid recursive calls
        }

        // Cross-instance dedup: el editor y el thumb de tab-tickets pueden
        // estar suscritos al MISMO ticket — sin esto los dos imprimirian
        // la comanda. Ticket.DoCommit emite un PrintClaim fresco por
        // commit; el primero que llama take() gana, los demas reciben
        // false y descartan su set de cambios local. El claim no necesita
        // limpieza: vive solo durante este emit.
        if (data?.claim && !data.claim.take()){
            this._ClearTicketChanges();
            return;
        }

        let _valid = this._ticket.IsRecent && (this._ticket.IsValid || (this._ticket.ToCancel && !this._ticket.IsReady));
        if (this._ticket.IsPrepay){
            _valid = _valid && this._ticket.IsPaid
        }

        if (_valid){
            this._ClearVoidChanges();        // remove void changes

            if (this._ticket.printed && this._ticketchanges){
                let _skip = !this._ticketchanges.modified;

                if (_skip && (this._ticketchanges.toInsert.length > 0)){
                    _skip = false;    // there are new products to be printed
                }
                
                if (_skip && (this._ticketchanges.toDelete.length > 0)){
                    _skip = false;    // there are deleted products to be printed
                }

                if (_skip && ((this._ticketchanges.toModify || []).length > 0)){
                    _skip = false;    // there are modified products to be printed
                }

                if (_skip){
                    this._ClearTicketChanges();
                    console.info("[TICKET CHANGE] No changes to print for ticket " + this._ticket.orderno);
                    return;
                }
            } 
            
            this._changeinfo = this._ChangeInfo();
            this._PrinterLog(this._changeinfo);

            if (Object.keys(this._changeinfo.printer).length == 0){
                this._ClearTicketChanges();     // nothing to print: still clear stale changes
                return;
            }

            // Clear changes BEFORE the async print to prevent stale __printchanges
            // from being read by components that mount during printing
            // (e.g. ui-ticket-thumbnail in LastTickets dropdown after AddLastTicket)
            this._ClearTicketChanges();

            // El setter de _printing dispara change.detectChanges() sincrono,
            // asi que cuando llegamos al setTimeout el elemento #printable ya
            // existe en el DOM (el *ngIf="_printing || _display" del template
            // se evalua con _printing=true). Usamos setTimeout(0) en vez del
            // _viewChecked Subject porque ngAfterViewChecked depende de que CD
            // corra antes de la destruccion del editor — para tickets de BARRA
            // donde la unica instancia es la del editor, esa CD puede no
            // ocurrir a tiempo y la comanda se pierde. setTimeout(0) sobrevive
            // al cierre del componente porque la referencia DOM persiste
            // mientras nada la libere.
            this._printing = true;

            // Capturar el ticket de ESTA comanda: en entrada continua this._ticket
            // salta al ticket siguiente (vacio) entre este punto y el setTimeout,
            // asi que dentro leemos el orderno de _forTicket, no de this._ticket.
            const _forTicket = this._ticket;

            setTimeout(async () => {
                try {
                    // Esperar confirmacion del commit en backend antes de
                    // mandar la comanda a la impresora. Si super.DoCommit
                    // falla, no se imprime nada — evita ticket fantasma
                    // en cocina. Equivalente a .13 donde _onCommit solo
                    // emitia dentro de if(_result).
                    if (data?.commitReady){
                        try { await data.commitReady; }
                        catch(e) {
                            console.warn("[CHANGE] commit failed — skipping comanda print", e);
                            return;
                        }
                    }

                    // Actualizar orderno: OnCommit emite antes de que
                    // CreateTicket resuelva → arriba pintamos con
                    // order=null. Solo tocamos ese campo (regenerar
                    // _changeinfo entero peta porque _ticketchanges ya
                    // esta null tras _ClearTicketChanges).
                    //
                    // Doble ruta: componente vivo → detectChanges;
                    // destruido (editor cerro tras emit) → mutamos el
                    // textContent del <td data-order-cell> directamente
                    // — la referencia DOM sobrevive al ngOnDestroy pero
                    // el CD tiraria ViewDestroyedError.
                    if (this._changeinfo){
                        // NO pisar con null: _ChangeInfo() ya puso el orderno del
                        // ticket. Este bloque solo debe RELLENARLO si llego tarde
                        // (ticket nuevo, orderno asignado tras el commit). En entrada
                        // continua this._ticket ya apunta al siguiente ticket
                        // (orderno null) cuando corre este setTimeout, y sobrescribia
                        // el bueno -> la comanda salia SIN orderno. Solo actualizamos
                        // si hay un valor real; si no, conservamos el de _ChangeInfo.
                        if (_forTicket.orderno){
                            this._changeinfo.order = _forTicket.orderno;
                        }

                        let _cdOk = false;
                        if (!this._destroyed){
                            try {
                                this.change.detectChanges();
                                _cdOk = true;
                            }
                            catch(e) { /* ViewDestroyedError race con ngOnDestroy */ }
                        }
                        if (!_cdOk && this._printable && this._printable.nativeElement){
                            const _cells = this._printable.nativeElement.querySelectorAll('[data-order-cell]');
                            const _text = this._changeinfo.order || '';
                            for (let _i = 0; _i < _cells.length; _i++){
                                _cells[_i].textContent = _text;
                            }
                        }
                    }

                    if (this._printable && this._printable.nativeElement){
                        let _promises = [];     // send all and wait for all to finish

                        for(let _printer of this.Printers){
                            for(let _child of this._printable.nativeElement.children){
                                if (_child.id == 'printing_' + _printer.objid + "|" + _printer.device){
                                    _promises.push(this.printer.SendChange(this._source, _forTicket, _printer, this.UntrustedHtml(_child)));
                                }
                            }
                        }

                        await Promise.all(_promises);

                        if (!_forTicket.printed){
                            _forTicket.printed = true;
                            _forTicket.DoCommit(false);
                        }
                    }
                }
                finally {
                    this._printing = false;
                    // Si el print async no marco printed (commit fail, sin DOM,
                    // etc.), liberar el lock para que el fallback/reintento pueda actuar.
                    // _forTicket (no this._ticket): en entrada continua this._ticket ya
                    // apunta al siguiente y marcariamos/liberariamos el ticket equivocado.
                    if (!_forTicket.printed){
                        (_forTicket as any).__paidProcessing = false;
                    }
                }
            }, 0);
        }
    }

    /************************/
    /* STARTS HERE          */
    /************************/

    private _place: Place = null;

    constructor(private lang: languageService, private change: ChangeDetectorRef, public platform: platformService, private printer: printService, private view: viewService) {
        super(lang, change);
    }

    private _subscriptions = [];

    ngOnInit(){
        // nothing to do
    }

    private _ReadTicketChanges(){
        this._ticketchanges = null;

        if ('__printchanges' in this._ticket){
            this._ticketchanges = this._ticket['__printchanges'];
        }
    }

    private _KeepTicketChanges(){
        if (this._ticketchanges){
            this._ticket['__printchanges'] = this._ticketchanges;
        }
    }

    private _ClearTicketChanges(){
        this._ticketchanges = null;

        if ('__printchanges' in this._ticket){
            delete this._ticket['__printchanges'];
        }
    }

    ngOnChanges(){
        this._place = this._ticket.place;

        for(let _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];

        if (this._ticket){
            // ticket not loaded, register to refresh
            if (!this._ticket.place){
                this._subscriptions.push(
                    this._ticket.OnRefresh.subscribe(
                    data => {
                        this.ngOnChanges();
                    })
                )
            }

            // ticket completelly loaded
            else {  
                this._ReadTicketChanges();  // recover the changes from the ticket instance

                this._subscriptions.push(
                    this._ticket.OnModify.subscribe(
                    data => {
                        this.AddChange(data);
                    })
                )
    
                this._subscriptions.push(
                    this._ticket.OnCancel.subscribe(
                    data => {
                        this._OnCancelTicket();
                    })
                )
    
                this._subscriptions.push(
                    this._ticket.OnCommit.subscribe(
                    data => {
                        this._OnChangePrint(data);
                    })
                )

                if (this._ticket.IsPrepay){
                    if (this._ticket.IsPaid){
                        // Ticket ya PD al montar (p.ej. sync): diferir al
                        // microtask para que DoPatchValues acabe de montar
                        // place/qrcode antes de IsPaidProcessOwner.
                        if (!this._ticket.printed){
                            queueMicrotask(() => {
                                if (this._destroyed || !this._ticket || this._ticket.printed){
                                    return;
                                }
                                this._OnPaidTicket();
                            });
                        }
                    }
                    else {
                        this._subscriptions.push(
                            this._ticket.OnPayment.subscribe(
                            data => {
                                // OnPayment es sincrono dentro de patchValue;
                                // diferir para evaluar el dueño con children listos.
                                queueMicrotask(() => {
                                    if (this._destroyed || !this._ticket){
                                        return;
                                    }
                                    this._OnPaidTicket();
                                });
                            })
                        );
                    }
                }
            }
        }
    }

    ngOnDestroy(){
        super.OnDestroy();

        this._destroyed = true;

        for(let _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];
    }

    /*********************************/
    /* PRINTER INFORMATION           */
    /*********************************/

    private get _TicketTill(){
        // ticket updated from a POS device / non multiple-POS
        if (this._place.till){
            return this._place.till;
        }

        // TABLE ticket: get this ticket assigned printer (by its table)
        if (this._ticket.qrcode){
            return this._ticket.qrcode.till;
        }

        // BAR ticket: updated from a device (not POS)
        let _device = this._ticket.session.deviceid;
        for (let _till of this._place.services){
            for (let _connect of _till.connects){
                if (_connect.device == _device){
                    return _till;   // this ticket's created session's till 
                }
            }
        }

        return null;
    }

    private get Printers(){
        let _printers = [];
        let _barprint = [];     // determine the valid BAR printers for this till

        let _till = this._TicketTill;
        if (_till){
            for(let _printer of _till.printers){
                if (_printer.IsValid){
                    _barprint.push(_printer);
                }
            }
        }

        // include all valid printers into the final list
        for (let _service of this._place.services){
            if(_service.IsValid){
                for (let _printer of _service.printers){
                    if (_printer.IsValid && ((_printer.type != 'BAR') || _barprint.includes(_printer))){
                        _printers.push(_printer);
                    }
                }
            }
        }

        return _printers;
    }

    /*********************************/
    /* CHANGE INFORMATION            */
    /*********************************/

    private _ticketchanges = null;
    
    private AddChange(change){
        if (!this._ticketchanges){
            this._ticketchanges = { toInsert: [], toDelete: [], toModify: [], modified: false, comments: null };
        }
        else if (!this._ticketchanges.toModify){
            this._ticketchanges.toModify = [];   // __printchanges de una version anterior sin toModify
        }

        let _modified = false;

        if ((change.operation == 'add') && (this._ticketchanges.toInsert.indexOf(change.product) == -1)){
            this._ticketchanges.toInsert.push(change.product);
            _modified = true;
        }

        if ((change.operation == 'del') && (this._ticketchanges.toDelete.indexOf(change.product) == -1)){
            // un borrado supersede una modificacion pendiente del mismo
            // producto: quitarlo de toModify para que no salga a la vez en
            // MODIFICADOS y ELIMINADOS en la misma comanda.
            let _modIdx = this._ticketchanges.toModify.indexOf(change.product);
            if (_modIdx != -1){
                this._ticketchanges.toModify.splice(_modIdx, 1);
            }

            let _idx = this._ticketchanges.toInsert.indexOf(change.product);
            if (_idx != -1){
                this._ticketchanges.toInsert.splice(_idx, 1);
            }
            else {
                // ELIMINADOS solo si cocina VIO este producto. this._ticket.printed
                // es del TICKET, no del producto: en entrada continua el ticket ya
                // esta impreso por productos anteriores, y un producto anadido +
                // borrado ESTA sesion (nunca enviado) salia en ELIMINADOS. Usamos la
                // señal per-producto (mismo patron que CartModify): persistido =
                // !ToInsert o copia con objid — un Copy() del editor tiene
                // ToInsert=true aunque venga de una fila con objid, de ahi el CopyOf.
                let _persisted = change.product
                              && (!change.product.ToInsert
                                  || !!(change.product.CopyOf && (change.product.CopyOf as TicketProduct).objid));

                if (this._ticket.printed && _persisted){
                    this._ticketchanges.toDelete.push(change.product);
                }
            }
            _modified = true;
        }

        // 'mod': modificacion de un producto YA visto por cocina (CartModify
        // cuando printed && !ToInsert). Si la comanda nunca llego a cocina, o
        // el producto sigue pendiente de primer envio, NO va a MODIFICADOS.
        if (change.operation == 'mod'){
            // Un producto que ya esta en toInsert es NUEVO (cocina no lo ha visto):
            // modificarlo sigue siendo NUEVO — el cambio ya se refleja en el objeto
            // al imprimir. Sin _inInsert aparecia a la vez en NUEVOS y MODIFICADOS.
            // this._ticket.printed es del TICKET, no del producto; en entrada
            // continua el ticket ya esta impreso y un producto recien anadido
            // colaba a MODIFICADOS. _persisted: mismo patron robusto que la rama del
            // (CopyOf cubre copias del editor con ToInsert=true pero objid real).
            let _inInsert = change.product
                         && (this._ticketchanges.toInsert.indexOf(change.product) != -1);
            let _persisted = change.product
                          && (!change.product.ToInsert
                              || !!(change.product.CopyOf && (change.product.CopyOf as TicketProduct).objid));
            let _kitchenKnows = this._ticket.printed && _persisted && !_inInsert;
            let _modIdx = this._ticketchanges.toModify.indexOf(change.product);

            if (_kitchenKnows){
                if (_modIdx == -1){
                    this._ticketchanges.toModify.push(change.product);
                    _modified = true;
                }
            }
            else {
                if (_modIdx != -1){
                    this._ticketchanges.toModify.splice(_modIdx, 1);
                    _modified = true;
                }

                if (change.product && (this._ticketchanges.toInsert.indexOf(change.product) == -1)){
                    this._ticketchanges.toInsert.push(change.product);
                    _modified = true;
                }
            }
        }

        if (change.operation == 'cmt'){
            this._ticketchanges.comments = this._ticket.comments;
            if (this._ticket.comments == ""){
                this._ticketchanges.comments = this.lang.tr('@place_print_comment_removed');
            }

            this._ticketchanges.modified = true;
            _modified = true;
        }

        // 'set' operations (e.g. status flag updates from DoCancel) reach here
        // but don't carry product-level changes — preserve _changeinfo cache so
        // a print already scheduled (toccl: cancel slip) doesn't lose its data
        if (_modified){
            this._KeepTicketChanges();
            this._changeinfo = null;
        }
    }

    private _OnPaidTicket(){
        // Pago por OTRA sesion (QR prepago via Stripe): el cliente no procesa
        // (tras Stripe el carrito no monta ui-change-print para PD). Un solo
        // TPV dueño (IsPaidProcessOwner) imprime comanda + commit fiscal.
        // El resto queda de respaldo tras la gracia si el dueño esta offline.
        if (this._ticket.PaidByAnotherSession && !this._ticket.IsPaidProcessOwner){
            let _ticket = this._ticket;
            let _jitter = Math.floor(Math.random() * 5000);
            // Si paidon ya es antiguo (eco sync tarde), no esperar otros 30s:
            // solo el resto de la gracia (clamp 0) + jitter.
            let _wait = _jitter;
            if (_ticket.paidon){
                let _elapsed = Math.max(0, (new Date()).getTime() - _ticket.paidon.getTime());
                _wait = Math.max(0, 30000 - _elapsed) + _jitter;
            }
            setTimeout(() => {
                if (this._destroyed || (this._ticket !== _ticket)){
                    return;
                }
                if (_ticket.IsPaid && !_ticket.printed && _ticket.RemotePaidGraceElapsed){
                    console.info("[TICKET CHANGE] Remote-paid fallback: owner did not process ticket " + _ticket.orderno);
                    this._ProcessPaidTicket();
                }
            }, _wait);
            console.info("[TICKET CHANGE] Skip remote-paid echo (waiting for owner) for ticket " + this._ticket.orderno);
            return;
        }

        this._ProcessPaidTicket();
    }

    private _ProcessPaidTicket(){
        // Dedup entre instancias ui-change-print del mismo ticket en este
        // dispositivo (thumb x2). Se libera si el intento no deja printed
        // (fallo/sin impresoras) para que otro intento o el fallback puedan
        // reintentar — un claim permanente dejaba el ticket sin comanda.
        if (this._ticket.printed){
            return;
        }
        if ((this._ticket as any).__paidProcessing){
            return;
        }
        (this._ticket as any).__paidProcessing = true;

        for(let _product of this._ticket.products){
            if (_product.IsValid){
                this.AddChange({
                    operation: 'add',
                    product: _product
                });
            }
        }

        if (this._ticket.payment == 'PAYLINE'){
            this._OnChangePrint();
            // Early-return sincrono de _OnChangePrint (no valido / sin
            // impresoras): no arranco _printing → liberar para reintentar.
            if (!this._printing && !this._ticket.printed){
                (this._ticket as any).__paidProcessing = false;
            }
        }
        else {
            (this._ticket as any).__paidProcessing = false;
        }
    }

    private _OnCancelTicket(){
        if (!this._ticket.IsRecent){
            return; // do not print changes for closed or old tickets
        }

        for(let _product of this._ticket.products){
            if (_product.status != 'DE'){
                this.AddChange({
                    operation: 'del',
                    product: _product
                });    
            }
        }

        this._OnChangePrint();
    }

    // Nombre del camarero del TICKET para la comanda (igual que el recibo): se
    // resuelve SIN traer la fila USER (no exponemos PII), casando el objid contra
    // place.waiters (alias del STAFF) y place.user (owner). Null si no resuelve.
    private _WaiterName(): string {
        let _wuser = this._ticket.waiter;
        let _place = this._place;
        if (!_wuser || !_place){
            return null;
        }
        for(let _staff of _place.waiters){
            if (_staff.IsValid && _staff.user && (_staff.user.objid == _wuser.objid)){
                return _staff.alias || (_staff.user.fullname || null);
            }
        }
        if (_place.user && (_place.user.objid == _wuser.objid)){
            return _place.user.fullname || null;
        }
        return null;
    }

    private _ChangeInfo(){
        let _changeinfo = {
            isnew: !this._ticket.printed,
            toccl: this._ticket.ToCancel,
            order: this._ticket.orderno,
            table: this._ticket.TableNo,    // alias de la mesa si lo tiene, si no el número
            waiter: this._WaiterName(),
            diners: this._ticket.diners,
            comments: null,
            date: null,
            time: null,
            printer: {}
        };

        this._GetTicketDate(_changeinfo);
        this._GetTicketCmnt(_changeinfo);
        this._GetTicketUdte(_changeinfo);

        return _changeinfo;
    }

    private _changeinfo = null;
    get ChangeInfo(){
        if (this._changeinfo == null){
            this._changeinfo = this._ChangeInfo();
        }
        return this._changeinfo;
    }

    Devices(_changeinfo){
        let _devices = [];

        for(let _device in _changeinfo.printer){
            _devices.push(_device);
        }

        return _devices;
    }

    private _GetTicketDate(_change){
        let date = new Date();

        let _date = '';
        _date += date.getDate() + '/';
        _date += (date.getMonth()+1) + '/';
        _date += date.getFullYear();

        let _time = '';
        _time += ('0' + date.getHours()).slice(-2) + ':';
        _time += ('0' + date.getMinutes()).slice(-2);

        _change.date = _date;
        _change.time = _time;
    }

    private _GetTicketCmnt(_change){
        if (!this._ticketchanges){
            return;     // no changes has been notified
        }

        _change.comments = this._ticketchanges.comments;
    }

    private _GetDirect(_ticketproduct){
        if (this._place.ExtendOrder){
            return null;
        }

        for(let _select of _ticketproduct.product.selects){
            // Mismo filter vacuous que ActiveDirectInfo (7a13d181) y
            // _GetSimilar (c9138243). Sin el skip: en una linea SIN
            // modificadores (`ticketproduct.options.length == 0`), un
            // direct legacy con 0 options validas mateha IsChosen por
            // la doble condicion vacuous (`0 == 0` + `[].every() == true`)
            // → _GetTicketChange renombra la comanda con `direct.name`
            // mientras el cajero ve el nombre base del producto
            // (Bugbot ronda 9 MEDIUM).
            if (_select.options.filter(o => o.IsValid).length == 0){
                continue;
            }
            if (_select.IsChosen(_ticketproduct)){
                return _select;
            }
        }

        return null;
    }

    private _GetSimilar(_ticketproduct){
        if (this._place.ExtendOrder){
            return [ null, null ];
        }

        let _similar = null;
        let _options = null

        for(let _select of _ticketproduct.product.selects){
            // Descartar selects sin options validas — mismo filter que
            // TicketProduct.ActiveDirectInfo (commit 7a13d181). Sin este
            // check, IsIncluded devolveria true por vacuous truth para
            // cualquier ticketproduct (`[].every() = true`), y con
            // first-match-wins un select cuya PRESELECTOPT AC apunta a
            // una PRODUCTOPT DE (FK huerfana logica: el admin marca la
            // productoption como DE pero la preselectopt no se cascadea)
            // usurparia la variante correcta. Reportado en carrito:
            // dividio 2 tercios de Estrella Galicia y aparecio BALTIC
            // PORTER. Este mismo bug era latente en cocina.
            if (_select.options.filter(o => o.IsValid).length == 0){
                continue;
            }

            if (_select.IsIncluded(_ticketproduct)){
                let _candidate_similar = _select;
                let _candidate_options = [];

                // add the options in the ticketproduct, but not in the select
                for (let _option of _ticketproduct.options){
                    if (!_select.HasSelection(_option.option)){
                        _candidate_options.push(_option);
                    }
                }

                // check if it is a better candidate than the current one
                if ((_options == null) || (_options.length > _candidate_options.length)){
                    _similar = _candidate_similar;
                    _options = _candidate_options;
                }
            }
        }

        return [ _similar, _options ];
    }

    private _GetTicketChange(_ticketproduct: TicketProduct){
        if (!_ticketproduct.product){
            return {
                separator: true
            };
        }
        else {
            let _ticketchange = {
                separator: false,
                groupin: _ticketproduct.group,

                product: _ticketproduct.product.name,
                code: _ticketproduct.product.code,
                options: [],
                comments: _ticketproduct.comments,
                weight: _ticketproduct.weight
            }

            let _namereplace = false;

            if (!_namereplace){
                let _direct = this._GetDirect(_ticketproduct);
                if (_direct != null){
                    _namereplace = true;

                    _ticketchange.product = _direct.name;
                    _ticketchange.code = _direct.code;
                }
            }

            if (!_namereplace){
                let [ _similar, _options ] = this._GetSimilar(_ticketproduct);
                if (_similar != null){
                    _namereplace = true;

                    _ticketchange.product = _similar.name;
                    _ticketchange.code = _similar.code;

                    for(let _option of _options){
                        _ticketchange.options.push(_option.option.name);
                    }    
                }
            }

            if (!_namereplace){
                for(let _option of _ticketproduct.options){
                    _ticketchange.options.push(_option.option.name);
                }    
            }

            if ((this._place.HideProductName) && (_ticketchange.code)){
                _ticketchange.product = '';   // ad-hoc change to hide the product name
            }

            return _ticketchange;    
        }
    }

    private _GroupTicketChanges(_changes, isnew){
        let _grouped = [];

        for(let _change of _changes){
            if (_change['separator']){
                _grouped.push({ count: 1, separator: true, value: null });
            }
            else {
                let _changestr = JSON.stringify(_change);

                let _found = false;
                for(let _group of _grouped){
                    if (_changestr == JSON.stringify(_group.value)){
                        _group.count++;
                        _found = true;
                    }
                }    
    
                if (!_found){
                    _grouped.push({ count: 1, separator: false, value: _change });
                }    
            }
        }

        return _grouped;
    }

    private _addSeparator(_ticketproduct, _ticketproducts){
        if (!_ticketproduct.Separator){
            return false;   // this item is not a separator
        }

        if (_ticketproducts.length > 0){
            let _last = _ticketproducts.length - 1;
            if (_ticketproducts[_last].Separator == false){
                return true;    // last item is not a separator
            }    
        }

        return false;
    }

    private _toPrinter(printer, ticketproducts){
        let _sortedproducts = ticketproducts;
        _sortedproducts.sort((a, b) => {
            return a.sort - b.sort;
        })

        if ((printer.type == 'BAR') || (printer.type == 'GEN')){
            let _print_updates = printer.updates;
            if (_print_updates && this._place.IsPOSDevice) {
                if ((this.view.Access != 'LOGIN') || [ 'QR', 'HM'].includes(this._ticket.source)){
                    _print_updates = true;  // this is a QR accesss, or ticket has been created by QR/HM access
                }
                else {
                    _print_updates = printer.posudts;   // this is a POS created ticket on a POS device
                }
            }

            if (_print_updates){
                if (printer.type == 'BAR'){
                    let _ticketproducts = [];

                    for(let _ticketproduct of _sortedproducts){
                        if (_ticketproduct.product == null){
                            if (this._addSeparator(_ticketproduct, _ticketproducts)){
                                _ticketproducts.push(_ticketproduct);
                            }
                        }
                        else {
                            if (_ticketproduct.product.type == 'KIT'){
                                continue;   // BAR printer: exclude kitchen products
                            }    

                            _ticketproducts.push(_ticketproduct);
                        }
                    }
        
                    return _ticketproducts;        
                }

                else {  // GEN printer: print all requested products
                    return _sortedproducts;
                }
            }

            return [];  // do not print updates
        }

        if (printer.type == 'KIT'){
            let _ticketproducts = [];

            for(let _ticketproduct of _sortedproducts){
                if (_ticketproduct.product == null){
                    if (this._addSeparator(_ticketproduct, _ticketproducts)){
                        _ticketproducts.push(_ticketproduct);
                    }
    
                    continue;
                }

                if (_ticketproduct.product.type == 'KIT'){
                    _ticketproducts.push(_ticketproduct);
                    continue;
                }
            }

            return _ticketproducts;
        }

        if (printer.type == 'SPC'){
            let _ticketproducts = [];

            for(let _ticketproduct of _sortedproducts){
                if (_ticketproduct.product == null){
                    if (this._addSeparator(_ticketproduct, _ticketproducts)){
                        _ticketproducts.push(_ticketproduct);
                    }
    
                    continue;
                }

                if (printer.products.some(_printerproduct => {
                    // Atajo asignado a la impresora: solo casa la línea cuyo atajo
                    // activo es ESE preselect (p.ej. "SOLO"), no todo el producto.
                    if (_printerproduct.select){
                        return (_ticketproduct.ActiveDirect == _printerproduct.select);
                    }
                    if (_printerproduct.product.isgroup){
                        return _ticketproduct.product.IsChild(_printerproduct.product);
                    }
                    else {
                        return (_printerproduct.product == _ticketproduct.product)
                    }
                })){
                    _ticketproducts.push(_ticketproduct);
                    continue;
                }
            }
            
            return _ticketproducts;
        }

        console.error("[PRINTER]: Unrecognized printer type '" + printer.type + "'");
        return [];
    }

    private get _TillPrinters(){
        let _printers = [];

        let _till = this._place.till;   // get this place printer service
        if (!_till){    // get this ticket assigned printer (by its table)
            _till = this._ticket.qrcode.till;
        }

        if (_till){
            for(let _printer of _till.printers){
                if (_printer.IsValid){
                    _printers.push(_printer);
                }
            }
        }

        return _printers;
    }

    private _GetTicketUdte(_change){
        if (!this._ticketchanges){
            return;     // no changes has been notified
        }

        for(let _printer of this.Printers){
            let _targetPrinter = (this._toPrinter(_printer, this._ticket.products).length > 0);

            // discard BAR pinters of oher tills 
            if (_targetPrinter){
                // TODO??
            }

            if (_targetPrinter){
                let _printerInsert = this._toPrinter(_printer, this._ticketchanges.toInsert);
                let _printerDelete = this._toPrinter(_printer, this._ticketchanges.toDelete);
                let _printerModify = this._toPrinter(_printer, this._ticketchanges.toModify || []);

                if (this._ticketchanges.modified || (_printerInsert.length > 0) || (_printerDelete.length > 0) || (_printerModify.length > 0)){
                    let _toInsert = [];
                    let _toDelete = [];
                    let _toModify = [];

                    for(let _ticketproduct of _printerInsert){
                        _toInsert.push(this._GetTicketChange(_ticketproduct));
                    }

                    for(let _ticketproduct of _printerDelete){
                        _toDelete.push(this._GetTicketChange(_ticketproduct));
                    }

                    for(let _ticketproduct of _printerModify){
                        _toModify.push(this._GetTicketChange(_ticketproduct));
                    }

                    _change.printer[_printer.objid + '|' + _printer.device] = {
                        toInsert: this._GroupTicketChanges(_toInsert, _change.isnew),
                        toDelete: this._GroupTicketChanges(_toDelete, _change.isnew),
                        toModify: this._GroupTicketChanges(_toModify, _change.isnew),
                    };
                }
            }
        }
    }
}
